import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const icon = L.icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41], shadowSize: [41, 41] });
export default function BusinessLocationPicker({ value, onChange, disabled }) {
 const element = useRef(null); const map = useRef(null); const marker = useRef(null);
 const current = useRef({ onChange, disabled }); current.current = { onChange, disabled };
 const [locating, setLocating] = useState(false); const [error, setError] = useState('');
 const [tileError, setTileError] = useState(false);
 const request = useRef(0);
 useEffect(() => {
  const instance = L.map(element.current, { scrollWheelZoom: false }).setView([0, 0], 2);
  map.current = instance;
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
   maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }).on('tileerror', () => setTileError(true)).addTo(instance);
  instance.on('click', event => {
   if (!current.current.disabled) {
    const point = event.latlng.wrap();
    current.current.onChange({ latitude: point.lat, longitude: point.lng });
   }
  });
  return () => { request.current += 1; instance.remove(); map.current = null; marker.current = null; };
 }, []);
 useEffect(() => {
  if (!value) { marker.current?.remove(); marker.current = null; return; }
  const point = [value.latitude, value.longitude];
  if (!marker.current) {
   marker.current = L.marker(point, { icon, draggable: !disabled, alt: 'Business location pin' }).addTo(map.current);
   marker.current.on('dragend', () => {
    if (!current.current.disabled) {
     const position = marker.current.getLatLng().wrap();
     current.current.onChange({ latitude: position.lat, longitude: position.lng });
    }
   });
  }
  marker.current.setLatLng(point);
  if (disabled) marker.current.dragging.disable(); else marker.current.dragging.enable();
  map.current.setView(point, Math.max(map.current.getZoom(), 16));
 }, [value, disabled]);
 const locate = () => {
  setError('');
  if (!navigator.geolocation) { setError('Your browser cannot share a location. Place the pin on the map instead.'); return; }
  const id = ++request.current;
  setLocating(true);
  navigator.geolocation.getCurrentPosition(position => {
   if (id !== request.current) return;
   setLocating(false);
   if (!current.current.disabled) current.current.onChange({ latitude: position.coords.latitude, longitude: position.coords.longitude });
  }, () => {
   if (id !== request.current) return;
   setLocating(false); setError('Could not get your location. Allow location access or place the pin on the map.');
  }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
 };
 return <section aria-label="Business location" className="space-y-3 rounded-lg border p-3">
  <p className="text-sm font-medium">Business location pin</p>
  <p className="text-xs text-gray-600">Use your current location when you are at the business, or zoom in and tap the map to place a pin. Drag the pin to adjust it.</p>
  <div ref={element} aria-label="Business location map" className="relative z-0 h-72 rounded-lg" />
  {tileError && <p role="status" className="text-xs text-amber-900">Map tiles could not load. Check your connection. You can still save your current location.</p>}
  <div className="flex flex-wrap gap-2">
   <button type="button" disabled={disabled || locating} onClick={locate} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50">{locating ? 'Finding location…' : 'Use my current location'}</button>
   <button type="button" disabled={disabled} onClick={() => { const point = map.current.getCenter().wrap(); onChange({ latitude: point.lat, longitude: point.lng }); }} className="rounded-lg border px-3 py-2 text-sm">Place pin at map centre</button>
   {value && <button type="button" disabled={disabled} onClick={() => { request.current += 1; setLocating(false); onChange(null); }} className="rounded-lg border px-3 py-2 text-sm">Remove pin</button>}
  </div>
  {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
  {value ? <p className="text-sm">Pin: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)} · <a href={`https://www.google.com/maps/search/?api=1&query=${value.latitude},${value.longitude}`} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline">Open in Maps</a></p> : <p className="text-xs text-gray-600">No location pin selected.</p>}
  <p className="text-xs text-gray-600">Choose Save business details below to save the pin.</p>
 </section>;
}
